import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import Input from '../../components/form/Input';

import axiosInstance from '../../services/axios';
import { resetPasswordUpdateRequired } from '../../redux/slices/employeeSlice';

// Password update schema with validation
const passwordUpdateSchema = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
    )
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

export default function EmployeePasswordUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employeeLoading, error, temporaryToken } = useSelector((state) => state.employee);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(passwordUpdateSchema),
  });

  const onSubmit = async (data) => {
    try {
      // Use the temporary token for the password update request
      const response = await axiosInstance.put('/employee/update/password', {
        password: data.password,
        confirmPassword: data.confirmPassword,
      }, {
        headers: {
          Authorization: `Bearer ${temporaryToken}`
        }
      });
      
      setUpdateSuccess(true);
      setUpdateError(null);
      
    //   Reset the password update required state after 2 seconds and navigate to login
      setTimeout(() => {
        dispatch(resetPasswordUpdateRequired());
        navigate('/employee-login');
      }, 2000);
    } catch (error) {
    //   setUpdateError(error.response?.data?.message || 'Failed to update password');
    }
  };

  const handleCancel = () => {
    dispatch(resetPasswordUpdateRequired());
    navigate('/employee-login');
  };

  return (
    <div style={{
      backgroundImage: 'linear-gradient(to top, #a18cd1 0%, #fbc2eb 100%)',
    }} className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Update Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Your password needs to be updated for security reasons
          </p>
        </div>
        
        {updateSuccess && (
          <div className="bg-green-50 text-green-700 p-3 rounded-md text-sm">
            Password updated successfully! Redirecting to login...
          </div>
        )}

        {(updateError || error) && (
          <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
            {updateError || error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm space-y-4">
            <Input
              label="New Password"
              type="password"
              {...register('password')}
              error={errors.password}
              placeholder="Enter your new password"
            />
            <Input
              label="Confirm Password"
              type="password"
              {...register('confirmPassword')}
              error={errors.confirmPassword}
              placeholder="Confirm your new password"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={employeeLoading}
              className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {employeeLoading ? 'Updating...' : 'Update Password'}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
